import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CloudDownload, ExpandMore, GraphicEq, Lock, MenuBook } from '@mui/icons-material';
import abacusService, {
  AbacusCourseStructureDTO,
  AbacusLevelDTO,
  AbacusPlacementResultDTO,
  AbacusTopicCardDTO,
  CompetitionView,
  LeaderboardView,
  TimedDrillView,
} from '../services/abacusService';
import {
  categoryLabel,
  isCurrentLevel,
  levelLockReason,
  practiceSummary,
  trackBadgeLabel,
} from '../utils/abacusCourseHome';
import {
  downloadLevelBundle,
  flushQueuedAttempts,
  OfflineQuotaError,
  removeDownloadedLevel,
  SyncStatus,
} from '../pwa/offlineBundle';

const AbacusCoursePage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState<AbacusCourseStructureDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloadingLevel, setDownloadingLevel] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [progressLevel, setProgressLevel] = useState<string | null>(null);
  const [quotaLevel, setQuotaLevel] = useState<string | null>(null);
  const [pendingDownload, setPendingDownload] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [placementAnswers, setPlacementAnswers] = useState<[number, number, number]>([0, 0, 0]);
  const [placement, setPlacement] = useState<AbacusPlacementResultDTO | null>(null);
  const [placementBusy, setPlacementBusy] = useState(false);
  const [sprint, setSprint] = useState<TimedDrillView | null>(null);
  const [sprintAnswer, setSprintAnswer] = useState('');
  const [sprintScore, setSprintScore] = useState<number | null>(null);
  const [board, setBoard] = useState<LeaderboardView>({ rows: [] });
  const [openEvents, setOpenEvents] = useState<CompetitionView[]>([]);
  const [sprintMessage, setSprintMessage] = useState<string | null>(null);

  const parsedCourseId = Number(courseId);

  useEffect(() => {
    const loadStructure = async () => {
      if (!parsedCourseId) return;
      const cacheKey = `abacus-structure-${parsedCourseId}`;
      try {
        setLoading(true);
        const data = await abacusService.getStudentCourseStructure(parsedCourseId);
        setStructure(data);
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (err: unknown) {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached && !navigator.onLine) {
          setStructure(JSON.parse(cached) as AbacusCourseStructureDTO);
          setError(null);
          return;
        }
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
        setError(message || 'Failed to load Abacus course.');
      } finally {
        setLoading(false);
      }
    };
    loadStructure();
  }, [parsedCourseId]);

  useEffect(() => {
    const loadGamification = async () => {
      try {
        const [nextBoard, events, latest] = await Promise.all([
          abacusService.getLeaderboard(),
          abacusService.openCompetitions(),
          abacusService.latestTimedDrill(),
        ]);
        setBoard(nextBoard);
        setOpenEvents(events);
        if (latest) {
          setSprintScore(latest.correctCount);
        }
      } catch {
        setBoard({ rows: [] });
      }
    };
    loadGamification();
  }, []);

  const startSprint = async () => {
    setSprintMessage(null);
    try {
      const started = await abacusService.startTimedDrill();
      setSprint(started);
      setSprintAnswer('');
      setSprintScore(started.correctCount);
    } catch {
      setSprintMessage('Could not start the 60-second sprint.');
    }
  };

  const submitSprint = async () => {
    if (!sprint?.attemptId || !sprint.questionId) return;
    const parsed = Number(sprintAnswer);
    if (!Number.isInteger(parsed)) {
      setSprintMessage('Enter a whole number.');
      return;
    }
    try {
      const next = await abacusService.answerTimedDrill(sprint.attemptId, sprint.questionId, parsed);
      setSprint(next.finished ? null : next);
      setSprintScore(next.correctCount);
      setSprintAnswer('');
      if (next.finished) {
        setSprintMessage('Sprint finished.');
      }
    } catch {
      setSprintMessage('Could not score that answer.');
    }
  };

  const refreshBoard = async () => {
    const nextBoard = await abacusService.getLeaderboard();
    setBoard(nextBoard);
  };

  useEffect(() => {
    const onOnline = () => {
      void flushQueuedAttempts().then((status) => {
        if (status) setSyncStatus(status);
      }).catch(() => undefined);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const handleDownloadLevel = async (levelCode: string) => {
    if (!parsedCourseId) return;
    try {
      setError(null);
      setQuotaLevel(null);
      setDownloadingLevel(levelCode);
      setProgressLevel(levelCode);
      setDownloadProgress(0);
      await downloadLevelBundle(parsedCourseId, levelCode, setDownloadProgress);
      setDownloadProgress(100);
    } catch (err: unknown) {
      if (err instanceof OfflineQuotaError) {
        setQuotaLevel(err.oldestLevelCode);
        setPendingDownload(levelCode);
        setError('Storage is full. Remove the oldest downloaded level and try again.');
      } else {
        setError('Could not start offline download for this level.');
      }
      setDownloadProgress(null);
    } finally {
      setDownloadingLevel(null);
    }
  };

  const removeOldest = async () => {
    if (!parsedCourseId || !quotaLevel) return;
    const retry = pendingDownload;
    await removeDownloadedLevel(parsedCourseId, quotaLevel);
    setQuotaLevel(null);
    setPendingDownload(null);
    setError(null);
    if (retry) await handleDownloadLevel(retry);
  };

  if (loading) {
    return (
      <Box minHeight="60vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!structure) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error ?? 'Abacus course is unavailable.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }} data-testid="abacus-course-home">
      <Stack spacing={1} mb={2}>
        <Typography variant="h4" fontWeight={700}>
          {structure.courseName}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip data-testid="abacus-track-badge" label={trackBadgeLabel(structure.track)} color="secondary" />
          {structure.currentLevelCode && (
            <Chip label={`Current level ${structure.currentLevelCode}`} color="primary" />
          )}
        </Stack>
        {error && <Alert severity="warning">{error}</Alert>}
        {syncStatus && (
          <Alert
            severity="success"
            data-testid="abacus-sync-status"
            data-client-attempt-id={syncStatus.clientAttemptId}
            data-attempt-id={String(syncStatus.attemptId)}
            data-duplicate={syncStatus.duplicate ? 'true' : 'false'}
          >
            Attempt synced
          </Alert>
        )}
        {quotaLevel && (
          <Alert severity="warning" data-testid="abacus-storage-quota">
            Remove the oldest downloaded level ({quotaLevel}) to free space.
            <Button data-testid="abacus-remove-oldest" onClick={() => void removeOldest()}>
              Remove oldest level
            </Button>
          </Alert>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="abacus-timed-drill">
        <Typography fontWeight={700}>60-second sprint</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Score is the number of correct answers. This does not complete a topic or a Test Week.
        </Typography>
        <Typography variant="body2" data-testid="abacus-timed-score">
          {sprintScore == null ? 'No sprint score yet' : `Sprint score ${sprintScore}`}
        </Typography>
        {sprint?.prompt && (
          <Typography sx={{ mt: 1 }} data-testid="abacus-timed-prompt">
            {sprint.prompt}
          </Typography>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <Button size="small" variant="contained" data-testid="abacus-timed-start" onClick={() => void startSprint()}>
            Start 60-second sprint
          </Button>
          <TextField
            size="small"
            label="Answer"
            value={sprintAnswer}
            data-testid="abacus-timed-answer"
            onChange={(event) => setSprintAnswer(event.target.value)}
            onPaste={(event) => event.preventDefault()}
            onDrop={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
                event.preventDefault();
              }
            }}
          />
          <Button size="small" variant="outlined" data-testid="abacus-timed-submit" onClick={() => void submitSprint()}>
            Submit answer
          </Button>
        </Stack>
        {sprintMessage && <Alert severity="info" sx={{ mt: 1 }}>{sprintMessage}</Alert>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="abacus-leaderboard">
        <Typography fontWeight={700}>Weekly Junior 1–3 board</Typography>
        <Typography variant="body2" color="text.secondary">
          Opt in to show an anonymized name. Rank does not unlock a level.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ my: 1 }}>
          <Button
            size="small"
            variant="contained"
            data-testid="abacus-leaderboard-opt-in"
            onClick={() => {
              void abacusService.setLeaderboardOptIn(true).then(refreshBoard).catch(() => setSprintMessage('Opt-in was refused.'));
            }}
          >
            Opt in
          </Button>
          <Button
            size="small"
            variant="outlined"
            data-testid="abacus-leaderboard-opt-out"
            onClick={() => {
              void abacusService.setLeaderboardOptIn(false).then(refreshBoard);
            }}
          >
            Opt out
          </Button>
        </Stack>
        {board.rows.length === 0 && (
          <Typography variant="body2" data-testid="abacus-leaderboard-empty">No opted-in scores this week.</Typography>
        )}
        {board.rows.map((row) => (
          <Typography key={`${row.rank}-${row.displayName}`} variant="body2" data-testid="abacus-leaderboard-row">
            {row.rank}. {row.displayName} — {row.score}
          </Typography>
        ))}
        {board.myRank != null && (
          <Typography variant="body2" data-testid="abacus-leaderboard-rank">Your rank {board.myRank}</Typography>
        )}
        {openEvents[0] && (
          <Button
            sx={{ mt: 1 }}
            size="small"
            variant="contained"
            data-testid="abacus-competition-join"
            onClick={() => {
              void abacusService.joinCompetition(openEvents[0].id).then(async () => {
                setSprintMessage(`Joined ${openEvents[0].title}`);
                setOpenEvents(await abacusService.openCompetitions());
              });
            }}
          >
            Join {openEvents[0].title}
          </Button>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="abacus-placement">
        <Typography fontWeight={700}>Not sure about Junior or Senior?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Three short answers suggest a track. Nothing changes until you confirm.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {['Used an abacus', 'Mental sums', 'Want a faster start'].map((label, index) => (
            <Button
              key={label}
              size="small"
              variant={placementAnswers[index] > 0 ? 'contained' : 'outlined'}
              onClick={() => {
                const next: [number, number, number] = [...placementAnswers];
                next[index] = (placementAnswers[index] + 1) % 3;
                setPlacementAnswers(next);
              }}
            >
              {label}: {placementAnswers[index]}
            </Button>
          ))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={placementBusy}
            onClick={() => {
              setPlacementBusy(true);
              abacusService.recommendPlacement([...placementAnswers])
                .then(setPlacement)
                .catch(() => setPlacement(null))
                .finally(() => setPlacementBusy(false));
            }}
          >
            See recommendation
          </Button>
          {placement && (
            <Button
              size="small"
              variant="outlined"
              disabled={placementBusy}
              onClick={() => {
                setPlacementBusy(true);
                abacusService.confirmPlacement(placement.recommendedTrack)
                  .then(setPlacement)
                  .catch(() => setPlacement(null))
                  .finally(() => setPlacementBusy(false));
              }}
            >
              Confirm {placement.recommendedTrack === 'SENIOR' ? 'Senior' : 'Junior'}
            </Button>
          )}
        </Stack>
        {placement && (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="abacus-placement-result">
            {placement.message}
          </Alert>
        )}
      </Paper>

      <Stack spacing={1.5}>
        {structure.levels.map((level) => (
          <LevelSection
            key={level.levelCode}
            level={level}
            current={isCurrentLevel(level.levelCode, structure.currentLevelCode)}
            downloading={downloadingLevel === level.levelCode}
            progress={progressLevel === level.levelCode ? downloadProgress : null}
            onDownload={() => handleDownloadLevel(level.levelCode)}
            onOpenTopic={(topicId, quiz) =>
              navigate(
                `/student-courses/${parsedCourseId}/abacus/topics/${topicId}${quiz ? '?mode=quiz' : ''}`
              )
            }
          />
        ))}
      </Stack>
    </Container>
  );
};

interface LevelSectionProps {
  level: AbacusLevelDTO;
  current: boolean;
  downloading: boolean;
  progress: number | null;
  onDownload: () => void;
  onOpenTopic: (topicId: number, quiz: boolean) => void;
}

const LevelSection: React.FC<LevelSectionProps> = ({
  level,
  current,
  downloading,
  progress,
  onDownload,
  onOpenTopic,
}) => {
  const lockText = levelLockReason(level);
  return (
    <Accordion
      defaultExpanded={current && level.unlocked}
      disableGutters
      data-testid={`abacus-level-${level.levelCode}`}
      data-current={current ? 'true' : 'false'}
      sx={{
        border: '1px solid',
        borderColor: current ? 'primary.main' : 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: '100%', pr: 1 }}>
          <Typography fontWeight={700}>{level.title}</Typography>
          {current && <Chip size="small" color="primary" label="Current" />}
          {!level.unlocked && <Chip size="small" icon={<Lock />} label="Locked" />}
          {level.unlocked && level.completed && <Chip size="small" color="success" label="Completed" />}
          <Typography variant="body2" color="text.secondary">
            {level.topicsCompleted}/{level.totalTopics} topics viewed
          </Typography>
          {lockText && (
            <Typography variant="body2" data-testid={`abacus-level-lock-${level.levelCode}`}>
              {lockText}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {level.categories.map((category) => (
            <Accordion
              key={`${level.levelCode}-${category.key}`}
              defaultExpanded={current && level.unlocked}
              disableGutters
              data-testid={`abacus-category-${level.levelCode}-${category.key}`}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>{categoryLabel(category.key, category.title)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {category.topics.map((topic) => (
                    <TopicCard
                      key={topic.topicId}
                      topic={topic}
                      levelLocked={!level.unlocked}
                      onOpenTopic={onOpenTopic}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
        {level.unlocked && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudDownload />}
              data-testid={`abacus-download-${level.levelCode}`}
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? 'Preparing bundle...' : `Download ${level.levelCode}`}
            </Button>
            {progress != null && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  data-testid="abacus-download-progress"
                  aria-valuenow={progress}
                />
                <Typography variant="body2" data-testid="abacus-download-status">
                  {progress >= 100 ? 'Download complete' : `Downloading ${progress}%`}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

interface TopicCardProps {
  topic: AbacusTopicCardDTO;
  levelLocked: boolean;
  onOpenTopic: (topicId: number, quiz: boolean) => void;
}

const TopicCard: React.FC<TopicCardProps> = ({ topic, levelLocked, onOpenTopic }) => {
  const blocked = levelLocked || Boolean(topic.locked);
  const canDrill = topic.contentMode === 'DRILL' && !topic.isTestWeek && !blocked;
  const canStartTestWeek = Boolean(topic.isTestWeek) && !blocked;
  return (
    <Box
      data-testid={`abacus-topic-${topic.topicId}`}
      data-content-mode={topic.contentMode}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}
    >
      <Stack spacing={0.75}>
        <Typography fontWeight={600}>{topic.title}</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={topic.viewed ? 'Viewed' : 'Not viewed'} color={topic.viewed ? 'success' : 'default'} />
          {topic.isTestWeek && (
            <Chip size="small" color={blocked ? 'warning' : 'secondary'} label={blocked ? 'Test Week locked' : 'Test Week ready'} />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" data-testid={`abacus-assessment-summary-${topic.topicId}`}>
          {practiceSummary(topic)}
        </Typography>
        {topic.lockReason && !levelLocked && (
          <Typography variant="body2" data-testid={`abacus-topic-lock-${topic.topicId}`}>
            {topic.lockReason}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<MenuBook />} onClick={() => onOpenTopic(topic.topicId, false)}>
            Learn
          </Button>
          {canDrill && (
            <Button
              size="small"
              variant="contained"
              startIcon={<GraphicEq />}
              onClick={() => onOpenTopic(topic.topicId, true)}
            >
              Start drill
            </Button>
          )}
          {canStartTestWeek && (
            <Button size="small" variant="contained" color="secondary" onClick={() => onOpenTopic(topic.topicId, true)}>
              Start Test Week
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default AbacusCoursePage;
